/**
 * StreetLampsModule — settlement street lights come ON at night.
 *
 * Two halves:
 *   Registry — placement code (CommonsSettlementsModule today; admin/world
 *     placements can join later) registers the WORLD position of every
 *     street-lamp head it instances, keyed by a source id so a streamed-out
 *     settlement cleanly retracts its lamps.
 *   Renderer — a fixed POOL of real PointLights (nearest lamps to the player)
 *     plus additive glow sprites (every lamp in sight radius), both driven by
 *     the sky's `uNightFactor` so they fade in across dusk and off at dawn.
 *
 * WHY a pool: the WebGPU forward path uploads every scene light to every lit
 * material — a city emits 30+ lamps and ten cities can be resident, so naive
 * one-PointLight-per-lamp would put hundreds of lights in the light loop. Ten
 * nearby real lights + cheap sprites for the rest reads like a lit street at
 * a bounded cost. Lights/sprites carry WORLD coordinates under the (possibly
 * origin-rebased) scene root — the same space the instanced lamps render in.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { bus } from '../bus.js';
import { atlas } from '../state.js';
import { uNightFactor } from './SkyModule.js';
import { applyLightCookie } from 'enginex/render/lighting/cookies/LightCookies.js';
import { activePreset } from '../lib/quality_tiers.js';
import { lampCookieValue, stage3Stats } from '../lib/stage3_fx.js';

export interface LampAnchor { x: number; y: number; z: number }

// ── Lamp registry ─────────────────────────────────────────────────────────────
const bySource = new Map<string, LampAnchor[]>();
let all: LampAnchor[] = [];
let version = 0;

// ── THE LAMPLIGHTER'S RESIDUE (society wave, 2026-09-19) ─────────────────────
// A town that staffs a lamplighter lights the share of its lamps he reached on
// his round (`settlement_residue.lampsLit`, in registry order = his route). The
// work shows after the worker has gone. A town with no round tonight keeps the
// FLOOR burning (self-lit heads) so no street ever goes black, and a settlement
// with no residue row at all (a hamlet: no such post) lights every lamp as before.
export const LAMPS_FLOOR_SHARE = 0.35;
/** A round older than this no longer counts (the lamps are trimmed nightly). */
export const LAMPS_ROUND_MAX_AGE_MS = 20 * 3600 * 1000;
const litShare = new Map<string, number>(); // sourceId → 0..1
/** The share of a source's lamps that burn tonight (pure: the harness pins it). */
export function lampShareFor(row: { lampsLit: number; lampsAtMs: number } | null, nowMs: number): number {
  if (!row) return 1;
  const fresh = nowMs - row.lampsAtMs < LAMPS_ROUND_MAX_AGE_MS;
  return Math.max(LAMPS_FLOOR_SHARE, Math.min(1, fresh ? row.lampsLit : 0));
}
/** How many of `count` lamps burn at `share` (registry order; at least one). */
export function litCount(count: number, share: number): number {
  return count <= 0 ? 0 : Math.max(1, Math.round(count * Math.max(0, Math.min(1, share))));
}
function reflatten(): void {
  all = [];
  for (const [src, anchors] of bySource) {
    const n = litCount(anchors.length, litShare.get(src) ?? 1);
    for (let i = 0; i < n; i++) all.push(anchors[i]);
  }
  version++;
}
/** Set a source's lit share (the module's residue poll calls this; a changed share re-flattens). */
export function setLampShare(sourceId: string, share: number): void {
  const prev = litShare.get(sourceId) ?? 1;
  if (Math.abs(prev - share) < 0.02) return;
  if (share >= 1) litShare.delete(sourceId); else litShare.set(sourceId, share);
  if (bySource.has(sourceId)) reflatten();
}

/** Register (or replace) a source's lamp-head world positions. */
export function registerLamps(sourceId: string, anchors: LampAnchor[]): void {
  if (anchors.length === 0) { unregisterLamps(sourceId); return; }
  bySource.set(sourceId, anchors);
  reflatten();
}

/** Remove a source's lamps (streamed-out settlement, cleared world). */
export function unregisterLamps(sourceId: string): void {
  if (bySource.delete(sourceId)) reflatten();
}

// ── Tuning ────────────────────────────────────────────────────────────────────
const MAX_LIGHTS = 10;       // real PointLights (the light-loop budget)
const MAX_GLOWS = 64;        // additive head-glow sprites
const LIGHT_RANGE_M = 30;    // PointLight cutoff distance
const LIGHT_CANDELA = 26;    // ≈ a 1000 lm street head under the ACES exposure
const LIGHT_PICK_M = 70;     // only lamps this close compete for a real light
const GLOW_PICK_M = 220;     // sprites visible out to here
const RETHINK_MOVE_M = 8;    // re-assign the pools after the player moves this far
const TICK_EVERY = 10;       // evaluate ~6×/s, not per frame

export class StreetLampsModule implements IModule {
  readonly name = 'StreetLamps';

  private root = new THREE.Group();
  private lights: Array<THREE.PointLight | THREE.SpotLight> = [];
  private glows: THREE.Sprite[] = [];
  private glowMat: THREE.SpriteMaterial | null = null;
  private glowTex: THREE.CanvasTexture | null = null;

  private frame = 0;
  private lastVersion = -1;
  private lastX = Infinity;
  private lastZ = Infinity;
  private wasNight = false;
  /** Pool slots currently assigned to a lamp (lights stay visible regardless). */
  private activeN = 0;

  init(app: App): void {
    app.world.scene.add(this.root);
    // The anchor registry is module-global with no world gate: admin-placed
    // Commons lamps kept lighting (real PointLights) inside personal worlds and
    // the Merchant Hive. Flush every source on a world hop; owners re-register.
    bus.on('atlas:world-cleared', () => { for (const k of [...bySource.keys()]) unregisterLamps(k); });

    // Warm sodium-ish head color — reads as "street light" against the blue night key.
    // LIGHT-SET IDENTITY LAW (live-traced 2026-07-31): a light's `visible` flip
    // changes the collected light set, which changes the lights-node cache key,
    // which invalidates EVERY lit material's pipeline — the next render then
    // rebuilds the whole scene's TSL graphs synchronously (7-12 s on real
    // hardware, repeating every RETHINK_MOVE_M of walking as the in-range lamp
    // count crossed the pool cap). All pool lights stay PERMANENTLY visible;
    // unused slots are intensity-0 and parked far below the world. Membership
    // never changes — only uniforms do. (The PMREM-identity law's sibling.)
    // Stage 3 (2026-09-09): with `lampCookies` the pool is SPOTLIGHTS pointing
    // down, each projecting the fixture's gobo (radial fall-off + louvres, a
    // procedural DataTexture) through three r185's SpotLightNode `map`. The
    // pool's KIND is fixed for the session (the light-set identity law: a
    // point↔spot swap re-links every lit material) — the menu row applies at
    // the next world entry.
    const cookie = activePreset().lampCookies ? StreetLampsModule.bakeCookie() : null;
    for (let i = 0; i < MAX_LIGHTS; i++) {
      let l: THREE.PointLight | THREE.SpotLight;
      if (cookie) {
        const sl = new THREE.SpotLight(0xffd9a3, 0, LIGHT_RANGE_M, 0.95, 0.55, 2);
        sl.target.position.set(0, -1, 0);
        sl.add(sl.target); // the target rides the head: straight down wherever the light sits
        applyLightCookie(sl, cookie, { kind: 'mask' });
        stage3Stats.cookies++;
        l = sl;
      } else {
        l = new THREE.PointLight(0xffd9a3, 0, LIGHT_RANGE_M, 2);
      }
      l.castShadow = false; // 10 shadow-casting point lights would be 60 cube faces
      l.position.set(0, -10000, 0);
      this.root.add(l);
      this.lights.push(l);
    }

    // One shared additive radial-gradient sprite material for every glow head.
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30);
    g.addColorStop(0, 'rgba(255,228,180,1)');
    g.addColorStop(0.35, 'rgba(255,205,130,0.55)');
    g.addColorStop(1, 'rgba(255,190,110,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    this.glowTex = new THREE.CanvasTexture(canvas);
    this.glowTex.colorSpace = THREE.SRGBColorSpace;
    this.glowMat = new THREE.SpriteMaterial({
      map: this.glowTex,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      opacity: 0,
    });
    markMrtOverlay(this.glowMat); // a glow sprite over the lamp, not a surface
    for (let i = 0; i < MAX_GLOWS; i++) {
      const s = new THREE.Sprite(this.glowMat);
      s.scale.set(1.7, 1.7, 1);
      s.visible = false;
      this.root.add(s);
      this.glows.push(s);
    }

    // After the sky uniforms (RenderSub/5) so this frame's nightFactor is current.
    app.world.addSystem(makeSystem('StreetLamps', Phase.RenderSub, 8, () => this.tick()));
  }

  /** The street-lamp head gobo (lib/stage3_fx `lampCookieValue` is the oracle). */
  private static bakeCookie(size = 128): THREE.DataTexture {
    const data = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      const v = Math.round(lampCookieValue((x + 0.5) / size, (y + 0.5) / size) * 255);
      const i = (y * size + x) * 4;
      data[i] = v; data[i + 1] = v; data[i + 2] = v; data[i + 3] = 255;
    }
    const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
    t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.needsUpdate = true;
    return t;
  }

  /** Every ~10 s: read the residue rows of the settlements whose lamps are registered (DB settlementId = site.id + 1). */
  private pollResidue(): void {
    const tbl = (atlas.conn?.db as unknown as { settlementResidue?: { settlementId: { find(id: bigint): { lampsLit: number; lampsAt: { microsSinceUnixEpoch: bigint } } | undefined } } } | undefined)?.settlementResidue;
    if (!tbl) return;
    const now = Date.now();
    for (const src of bySource.keys()) {
      if (!src.startsWith('settle:')) continue;
      const siteId = Number(src.slice(7));
      if (!Number.isFinite(siteId)) continue;
      const row = tbl.settlementId.find(BigInt(siteId + 1));
      setLampShare(src, lampShareFor(row ? { lampsLit: row.lampsLit, lampsAtMs: Number(row.lampsAt.microsSinceUnixEpoch / 1000n) } : null, now));
    }
  }

  private tick(): void {
    if (++this.frame % TICK_EVERY !== 0) return;
    if (this.frame % (TICK_EVERY * 60) === 0) this.pollResidue();
    if (atlas.spaceFlight) {
      // The sector owns space lighting; lamp coords overlap sector coords, so
      // a night refresh here re-lit "nearby" lamps inside the space world.
      if (this.wasNight) {
        this.parkAll();
        for (const s of this.glows) s.visible = false;
        this.wasNight = false;
      }
      return;
    }
    const night = (uNightFactor as unknown as { value: number }).value;
    const isNight = night > 0.04;

    if (!isNight) {
      if (this.wasNight) { // dawn crossing → switch everything off once
        this.parkAll();
        for (const s of this.glows) s.visible = false;
        this.wasNight = false;
      }
      return;
    }

    // Fade the shared knobs every evaluation (cheap — uniforms only).
    if (this.glowMat) this.glowMat.opacity = 0.85 * night;
    for (let i = 0; i < this.activeN; i++) this.lights[i].intensity = LIGHT_CANDELA * night;

    // Re-pick which lamps own the pools only when the world or the player moved.
    const px = atlas.localX, pz = atlas.localZ;
    const moved = Math.hypot(px - this.lastX, pz - this.lastZ) > RETHINK_MOVE_M;
    if (version === this.lastVersion && !moved && this.wasNight) return;
    this.lastVersion = version;
    this.lastX = px;
    this.lastZ = pz;
    this.wasNight = true;

    // Nearest-first among lamps in sight radius (settlement streaming already
    // bounds `all` to a few hundred anchors — a sort at 6 Hz-on-move is noise).
    const near = all
      .map((a) => ({ a, d2: (a.x - px) * (a.x - px) + (a.z - pz) * (a.z - pz) }))
      .filter((e) => e.d2 < GLOW_PICK_M * GLOW_PICK_M)
      .sort((x, y) => x.d2 - y.d2);

    let li = 0;
    for (const e of near) {
      if (li >= MAX_LIGHTS || e.d2 > LIGHT_PICK_M * LIGHT_PICK_M) break;
      const l = this.lights[li++];
      l.position.set(e.a.x, e.a.y, e.a.z);
      l.intensity = LIGHT_CANDELA * night;
    }
    // Unused slots: uniforms only — NEVER visibility (the light-set identity law).
    for (let i = li; i < MAX_LIGHTS; i++) {
      this.lights[i].intensity = 0;
      this.lights[i].position.set(0, -10000, 0);
    }
    this.activeN = li;

    for (let i = 0; i < MAX_GLOWS; i++) {
      const s = this.glows[i];
      if (i < near.length) {
        const a = near[i].a;
        s.position.set(a.x, a.y, a.z);
        s.visible = true;
      } else s.visible = false;
    }
  }

  /** All slots to intensity 0, parked — the lights stay in the scene, visible. */
  private parkAll(): void {
    for (const l of this.lights) {
      l.intensity = 0;
      l.position.set(0, -10000, 0);
    }
    this.activeN = 0;
  }

  dispose(): void {
    this.root.parent?.remove(this.root);
    this.glowMat?.dispose();
    this.glowTex?.dispose();
    this.lights.length = 0;
    this.glows.length = 0;
  }
}
